import Phaser from "phaser";
import i18next from "i18next";
import { COLORS, hexToPhaser } from "../../../ui/colors";
import { WindowManager } from "../../../ui/window-manager";
import { BaseWindow, BaseWindowConfig } from "../../../ui/windows/base-window";
import { getSafeAreaInsetLeft } from "../../../utils/mobile-utils";
import { ModernButton } from "../../../ui/modern-button";
import { LeaderboardEntry } from "../../../models/common";

const ROW_HEIGHT = 44;
const ROW_PADDING_X = 12;
const HEADER_EXTRA = 56; // height below the base window title bar before list starts

export class RanksWindow extends BaseWindow {
  private scrollContainer: Phaser.GameObjects.Container | null = null;
  private maskGraphics: Phaser.GameObjects.Graphics | null = null;
  private scrollY: number = 0;
  private maxScrollY: number = 0;
  private isDragging: boolean = false;
  private dragStartY: number = 0;
  private dragStartScrollY: number = 0;
  private pointerDownHandler: ((pointer: Phaser.Input.Pointer) => void) | null = null;
  private pointerMoveHandler: ((pointer: Phaser.Input.Pointer) => void) | null = null;
  private pointerUpHandler: ((pointer: Phaser.Input.Pointer) => void) | null = null;
  private onCloseCallback: (() => void) | null = null;
  private closeButtonObj: ModernButton | null = null;

  constructor(scene: Phaser.Scene, _windowManager: WindowManager) {
    super(scene);
  }

  protected getLayoutOverrides(_config: BaseWindowConfig): Partial<{ x: number; y: number; width: number; height: number }> {
    const gameWidth = this.scene.scale.width;
    const gameHeight = this.scene.scale.height;
    const safeLeft = getSafeAreaInsetLeft();
    const leftMargin = Math.max(100, 16 + safeLeft + 48 + 16);
    const width = gameWidth - leftMargin - 48;
    // Center the window horizontally instead of anchoring it to the left margin.
    const x = (gameWidth - width) / 2;
    const y = 32;
    return { x, y, width, height: gameHeight - y - 20 };
  }

  show(onClose?: () => void) {
    this.hide();
    this.onCloseCallback = onClose ?? null;

    const baseElements = this.createBaseWindow({ title: `${i18next.t("ui:leaderboard")} - PVP`, side: "center" });
    const bounds = this.getWindowBounds()!;
    const { x, y, width, height } = bounds;

    const elements: Phaser.GameObjects.GameObject[] = [...baseElements];

    // Loading indicator
    const loadingText = this.scene.add
      .text(x + width / 2, y + height / 2, "Loading...", {
        fontSize: "20px",
        color: COLORS.WHITE,
        fontFamily: "Anton",
      })
      .setOrigin(0.5)
      .setScrollFactor(0);
    elements.push(loadingText);

    this.container = this.scene.add.container(0, 0, elements);
    this.addContainerToParent(this.container);
    if (this.uiLayer) this.uiLayer.bringToTop(this.container);
    this.visible = true;

    // Back button floats above everything — added to uiLayer directly so it always
    // renders on top of the scroll container regardless of buildList timing.
    this.closeButtonObj = new ModernButton({
      scene: this.scene,
      x: x + width / 2,
      y: y + height - 30,
      text: i18next.t("ui:buttons.back"),
      style: "secondary",
      onClick: () => {
        if (this.onCloseCallback) this.onCloseCallback();
      },
    });
    this.closeButtonObj.setScrollFactor(0);
    if (this.uiLayer) this.uiLayer.add(this.closeButtonObj as unknown as Phaser.GameObjects.GameObject);

    // Request leaderboard data from server
    window.socket?.emit(
      "request-leaderboard",
      {},
      (response: { success: boolean; entries: LeaderboardEntry[] }) => {
        if (!this.visible || !this.container) return;
        loadingText.destroy();
        if (response.success) {
          this.buildList(response.entries, bounds);
        } else {
          const errText = this.scene.add
            .text(x + width / 2, y + height / 2, "Failed to load leaderboard.", {
              fontSize: "16px",
              color: COLORS.RED,
              fontFamily: "Anton",
            })
            .setOrigin(0.5)
            .setScrollFactor(0);
          this.container.add(errText);
        }
      },
    );
  }

  private buildList(
    entries: LeaderboardEntry[],
    bounds: { x: number; y: number; width: number; height: number },
  ) {
    if (!this.container) return;

    const { x, y, width, height } = bounds;
    const listStartX = x;
    const listStartY = y + this.CONTENT_PADDING_Y + HEADER_EXTRA;
    const listWidth = width;
    const listHeight = height - this.CONTENT_PADDING_Y - HEADER_EXTRA;

    // Column header
    const headerBg = this.scene.add.graphics();
    headerBg.fillStyle(0x1a3550, 0.9);
    headerBg.fillRect(listStartX, listStartY - ROW_HEIGHT, listWidth, ROW_HEIGHT);
    headerBg.setScrollFactor(0);
    this.container.add(headerBg);

    const colRankX = listStartX + ROW_PADDING_X + 28;
    const colNameX = listStartX + ROW_PADDING_X + 90;
    const colXpX = listStartX + listWidth - ROW_PADDING_X;

    const makeHeaderText = (text: string, tx: number, origin: [number, number]) =>
      this.scene.add
        .text(tx, listStartY - ROW_HEIGHT / 2, text, {
          fontSize: "15px",
          color: COLORS.YELLOW,
          fontFamily: "Anton",
        })
        .setOrigin(...origin)
        .setScrollFactor(0);

    this.container.add(makeHeaderText("#", colRankX, [0.5, 0.5]));
    this.container.add(makeHeaderText("PLAYER", colNameX, [0, 0.5]));
    this.container.add(makeHeaderText("PTS", colXpX, [1, 0.5]));

    // Clipping mask for the scrollable area
    this.maskGraphics = this.scene.add.graphics();
    this.maskGraphics.fillRect(listStartX, listStartY, listWidth, listHeight);
    const mask = this.maskGraphics.createGeometryMask();

    // Scrollable container (holds all rows)
    this.scrollContainer = this.scene.add.container(0, 0);
    this.scrollContainer.setScrollFactor(0);
    this.scrollContainer.setMask(mask);

    const totalContentHeight = entries.length * ROW_HEIGHT;
    this.maxScrollY = Math.max(0, totalContentHeight - listHeight);
    this.scrollY = 0;

    // Detect the first rank gap after the top-10 — this is where the section separator goes.
    // When the player's rank is within 30 of rank 1, the sections naturally overlap and no
    // separator is needed (separatorIndex stays -1).
    let separatorIndex = -1;
    for (let i = 10; i < entries.length; i++) {
      if (entries[i].rank - entries[i - 1].rank > 1) {
        separatorIndex = i;
        break;
      }
    }

    let playerRowBg: Phaser.GameObjects.Graphics | null = null;
    let playerRowIndex = -1;

    entries.forEach((entry, i) => {
      const rowY = listStartY + i * ROW_HEIGHT;
      const isHighlighted = entry.isCurrentPlayer;

      // Draw thick yellow separator line between the two sections
      if (i === separatorIndex) {
        const sepLine = this.scene.add.graphics();
        sepLine.lineStyle(4, hexToPhaser(COLORS.YELLOW), 1.0);
        sepLine.lineBetween(listStartX, rowY - 3, listStartX + listWidth, rowY - 3);
        sepLine.setScrollFactor(0);
        this.scrollContainer!.add(sepLine);
      }

      const rowBg = this.scene.add.graphics();
      if (isHighlighted) {
        rowBg.fillStyle(0x2a5530, 0.9);
      } else {
        rowBg.fillStyle(i % 2 === 0 ? 0x2a4060 : 0x213450, 0.85);
      }
      rowBg.fillRect(listStartX, rowY, listWidth, ROW_HEIGHT - 2);
      rowBg.setScrollFactor(0);
      this.scrollContainer!.add(rowBg);

      if (isHighlighted) {
        playerRowBg = rowBg;
        playerRowIndex = i;
      }

      const textColor = isHighlighted ? COLORS.GREEN : COLORS.WHITE;
      const rankColor = entry.rank <= 3 ? COLORS.YELLOW : textColor;

      const rankText = this.scene.add
        .text(colRankX, rowY + ROW_HEIGHT / 2, `${entry.rank}`, {
          fontSize: "16px",
          color: rankColor,
          fontFamily: "Anton",
        })
        .setOrigin(0.5, 0.5)
        .setScrollFactor(0);
      this.scrollContainer!.add(rankText);

      const nameText = this.scene.add
        .text(colNameX, rowY + ROW_HEIGHT / 2, entry.name, {
          fontSize: "16px",
          color: textColor,
          fontFamily: "Anton",
        })
        .setOrigin(0, 0.5)
        .setScrollFactor(0);
      this.scrollContainer!.add(nameText);

      const xpText = this.scene.add
        .text(colXpX, rowY + ROW_HEIGHT / 2, entry.trophies.toLocaleString(), {
          fontSize: "16px",
          color: textColor,
          fontFamily: "Anton",
        })
        .setOrigin(1, 0.5)
        .setScrollFactor(0);
      this.scrollContainer!.add(xpText);
    });

    this.uiLayer.add(this.scrollContainer);

    // Bring the back button above the scroll container
    if (this.closeButtonObj && this.uiLayer) {
      this.uiLayer.bringToTop(this.closeButtonObj as unknown as Phaser.GameObjects.GameObject);
    }
    // Start at the top, then animate down to centre the player's row.
    this.scrollY = 0;
    this.applyScrollOffset(listStartY);

    if (playerRowIndex >= 0) {
      const targetScrollY = Phaser.Math.Clamp(
        playerRowIndex * ROW_HEIGHT + ROW_HEIGHT / 2 - listHeight / 2,
        0,
        this.maxScrollY,
      );

      // Only animate if the player is not already visible at the top
      if (targetScrollY > 0) {
        const scrollObj = { value: 0 };
        this.scene.tweens.add({
          targets: scrollObj,
          value: targetScrollY,
          duration: 900,
          delay: 300,
          ease: "Cubic.easeInOut",
          onUpdate: () => {
            this.scrollY = scrollObj.value;
            this.applyScrollOffset(listStartY);
          },
          onComplete: () => {
            if (playerRowBg) {
              this.scene.tweens.add({
                targets: playerRowBg,
                alpha: { from: 0.25, to: 1.0 },
                duration: 350,
                yoyo: true,
                repeat: 3,
                ease: "Sine.easeInOut",
              });
            }
          },
        });
      } else if (playerRowBg) {
        // Player is already in the top section — just pulse immediately
        this.scene.tweens.add({
          targets: playerRowBg,
          alpha: { from: 0.25, to: 1.0 },
          duration: 350,
          delay: 300,
          yoyo: true,
          repeat: 3,
          ease: "Sine.easeInOut",
        });
      }
    }

    // Scroll via mouse wheel
    this.scene.input.on("wheel", (_pointer: Phaser.Input.Pointer, _gameObjects: any, _deltaX: number, deltaY: number) => {
      if (!this.visible) return;
      this.applyScroll(deltaY * 0.5, listStartY);
    });

    // Scroll via touch / mouse drag — use global scene events with a bounds check so
    // the handler works regardless of which display-list layer is on top.
    this.pointerDownHandler = (pointer: Phaser.Input.Pointer) => {
      if (!this.visible) return;
      if (
        pointer.x >= listStartX &&
        pointer.x <= listStartX + listWidth &&
        pointer.y >= listStartY &&
        pointer.y <= listStartY + listHeight
      ) {
        this.isDragging = true;
        this.dragStartY = pointer.y;
        this.dragStartScrollY = this.scrollY;
      }
    };
    this.scene.input.on("pointerdown", this.pointerDownHandler);

    this.pointerMoveHandler = (pointer: Phaser.Input.Pointer) => {
      if (!this.isDragging) return;
      const delta = this.dragStartY - pointer.y;
      this.scrollY = Phaser.Math.Clamp(this.dragStartScrollY + delta, 0, this.maxScrollY);
      this.applyScrollOffset(listStartY);
    };

    this.pointerUpHandler = () => {
      this.isDragging = false;
    };

    this.scene.input.on("pointermove", this.pointerMoveHandler);
    this.scene.input.on("pointerup", this.pointerUpHandler);
  }

  private applyScroll(delta: number, listStartY: number) {
    this.scrollY = Phaser.Math.Clamp(this.scrollY + delta, 0, this.maxScrollY);
    this.applyScrollOffset(listStartY);
  }

  private applyScrollOffset(listStartY: number) {
    if (!this.scrollContainer) return;
    this.scrollContainer.setY(-this.scrollY);
    // Redraw mask to keep it aligned to the clip area
    if (this.maskGraphics) {
      this.maskGraphics.clear();
      const bounds = this.getWindowBounds();
      if (!bounds) return;
      const { x, y, width, height } = bounds;
      const ly = y + this.CONTENT_PADDING_Y + HEADER_EXTRA;
      const lh = height - this.CONTENT_PADDING_Y - HEADER_EXTRA;
      this.maskGraphics.fillRect(x, ly, width, lh);
    }
  }

  hide() {
    this.onCloseCallback = null;
    // Remove scroll listeners before destroying
    if (this.pointerDownHandler) {
      this.scene?.input?.off("pointerdown", this.pointerDownHandler);
      this.pointerDownHandler = null;
    }
    if (this.pointerMoveHandler) {
      this.scene?.input?.off("pointermove", this.pointerMoveHandler);
      this.pointerMoveHandler = null;
    }
    if (this.pointerUpHandler) {
      this.scene?.input?.off("pointerup", this.pointerUpHandler);
      this.pointerUpHandler = null;
    }
    this.scene?.input?.off("wheel");
    this.isDragging = false;

    if (this.scrollContainer) {
      this.scrollContainer.destroy();
      this.scrollContainer = null;
    }
    if (this.maskGraphics) {
      this.maskGraphics.destroy();
      this.maskGraphics = null;
    }
    if (this.closeButtonObj) {
      (this.closeButtonObj as unknown as Phaser.GameObjects.GameObject).destroy();
      this.closeButtonObj = null;
    }

    super.hide();
  }
}
